/**
 * A seção da conferência facial na análise preliminar (E80.F2.T05 — ADR-0079).
 *
 * Entra logo depois das fotos do signatário: primeiro o perito escolhe o par,
 * aqui ele lê o que a comparação observou.
 *
 * Cinco estados, e cada um diz o que fazer a seguir:
 *   1. **não se aplica** — falta foto escolhida em algum grupo. Ausência
 *      declarada, nunca erro (§2.5); o botão nem aparece.
 *   2. **pronta** — o par está escolhido e a conferência ainda não foi feita.
 *   3. **em curso** — a comparação está rodando.
 *   4. **parecer emitido** — o painel de comparação (com a CONCLUSÃO no rodapé),
 *      as ressalvas e a proveniência (E90.F4: nada acima da tabela).
 *   5. **indisponível** — o serviço recusou; a tela diz o que houve e mantém a
 *      retentativa (E70.F2), nunca um erro técnico na frente do perito.
 *
 * A leitura verbal vem RESOLVIDA do servidor. Derivá-la aqui — "tem parecer?
 * então mostre isto" — deixaria a tela e o backend livres para discordar sobre
 * o mesmo estado, que é exatamente o que o veredito de fonte única (E59) existe
 * para impedir.
 *
 * `versaoDasFotos` é o gatilho de releitura: quando o perito troca a foto
 * escolhida na seção de cima, o par muda e o `par_mudou` deste parecer também.
 * Sem o gatilho, o aviso de troca só apareceria depois de recarregar a página.
 *
 * E184.F4 (GUS-145 item 5, ADR-0136 §4): com parecer, "Refazer análise morfológica" chama a mesma
 * comparação sobre o par eleito, mesmo com documento já concluído — o servidor pede a preparação,
 * que refaz o exame e o relatório de riscos sem clique, com as edições salvas; a conferência final
 * da versão anterior deixa de valer, e o parecer anterior fica no histórico.
 */
import { useCallback, useEffect, useState, type ReactNode } from 'react';

import { Blueprint, Button } from '@/components/ui';
import { ptBR } from '@/i18n/pt-BR';

import { PainelDaConferencia } from './PainelDaConferencia';
import { useApiClient } from '@/lib/api';
import {
  executarConferenciaFacial,
  lerConferenciaFacial,
  type ConferenciaFacialData,
  type ConferenciaFacialParecerData,
  type FotoConferidaData,
} from '@/lib/conferencia_facial';
import { ExameEletronicoError } from '@/lib/exame_eletronico';
import { formatarDataHora } from '@/lib/trilha_format';
import { useTrocaDeCaso } from '@/pages/useTrocaDeCaso';

const t = ptBR.exameEletronico.conferenciaFacial;
const tf = ptBR.exameEletronico.fotosDoSignatario;

const _MENSAGEM: Record<string, string> = {
  conferencia_sem_par: t.erros.conferencia_sem_par,
  // E200.F2 (GUS-220, ADR-0143 §3): a análise já roda no servidor; a tela espera e relê.
  conferencia_em_andamento: t.erros.conferencia_em_andamento,
  foto_integridade: t.erros.foto_integridade,
  foto_indisponivel: t.erros.foto_indisponivel,
  conferencia_indisponivel: t.erros.conferencia_indisponivel,
  provider_auth: t.erros.provider_auth,
  provider_credit: t.erros.provider_credit,
  sem_chave: t.erros.sem_chave,
  network: t.erros.network,
};

function mensagemDoErro(err: unknown): string {
  if (err instanceof ExameEletronicoError) return _MENSAGEM[err.reason] ?? t.erros.generico;
  return t.erros.generico;
}

/** A cor da leitura acompanha o que ela afirma, e o texto nunca depende dela. */
const _TOM: Record<string, string> = {
  elementos_convergentes: 'text-accent-700',
  elementos_divergentes: 'text-fg-danger',
  nao_foi_possivel_conferir: 'text-fg-muted',
  nao_se_aplica: 'text-fg-dim',
  nao_demonstrada: 'text-fg-muted',
};

interface ListaProps {
  titulo: string;
  vazio: string;
  itens: string[];
  testid: string;
}

function Lista({ titulo, vazio, itens, testid }: ListaProps): JSX.Element {
  return (
    <div data-testid={testid}>
      <h4 className="text-body-sm text-fg">{titulo}</h4>
      {itens.length === 0 ? (
        <p className="text-body-sm text-fg-dim">{vazio}</p>
      ) : (
        <ul className="list-disc space-y-sc-1 pl-sc-4">
          {itens.map((item) => (
            <li key={item} className="max-w-prose text-body-sm text-fg-muted">
              {item}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/** Rótulo e valor de um campo da proveniência (E205.F5.T01: campos rotulados, numa grade). */
function Campo({ rotulo, children }: { rotulo: string; children: ReactNode }): JSX.Element {
  return (
    <>
      <dt className="text-caption uppercase tracking-[0.08em] text-fg-muted">{rotulo}</dt>
      <dd className="text-body-sm text-fg">{children}</dd>
    </>
  );
}

function FotoComparada({ foto }: { foto: FotoConferidaData }): JSX.Element {
  return (
    <div className="min-w-0 space-y-1" data-testid={`conferencia-foto-${foto.grupo}`}>
      <Campo rotulo={tf.grupos[foto.grupo].titulo}>{foto.nome_arquivo}</Campo>
      <Campo rotulo={t.proveniencia.codigo}>
        <span className="break-all font-mono text-caption">{foto.content_sha256}</span>
      </Campo>
    </div>
  );
}

/**
 * As fotos comparadas e quando (E205.F5.T01, ADR-0147 §2.6): "Código de conferência" e "Conferido
 * em" em campos rotulados, numa grade, em vez de texto solto embaixo do painel.
 */
function Proveniencia({ parecer }: { parecer: ConferenciaFacialParecerData }): JSX.Element {
  return (
    <section aria-label={t.provenienciaTitulo} data-testid="conferencia-proveniencia">
      <h4 className="text-body-sm text-fg">{t.provenienciaTitulo}</h4>
      <dl className="mt-sc-1 grid gap-x-sc-6 gap-y-sc-3 md:grid-cols-[minmax(0,1fr)_minmax(0,1fr)_auto]">
        {/* E191.F1: a ordem do painel — formalização, depois documento de identidade. */}
        <FotoComparada foto={parecer.par.ccb} />
        <FotoComparada foto={parecer.par.documento_identidade} />
        <div className="space-y-1" data-testid="conferencia-feita-em">
          <Campo rotulo={t.proveniencia.conferidoEm}>{formatarDataHora(parecer.created_at)}</Campo>
        </div>
      </dl>
    </section>
  );
}

/** O parecer gravado: o painel, as ressalvas e a proveniência. E123.F3: a pré-análise o reusa. */
export function ParecerDaConferencia({
  caseId,
  parecer,
  parMudou,
  recorteMudou = false,
}: {
  caseId: string;
  parecer: ConferenciaFacialParecerData;
  parMudou: boolean;
  /** E141.F2 (ADR-0079 E.26): o mesmo par, com outra caixa ou outro giro desde o parecer. */
  recorteMudou?: boolean;
}): JSX.Element {
  const semAfirmacao =
    parecer.leitura === 'nao_foi_possivel_conferir' &&
    parecer.convergencias.length === 0 &&
    parecer.divergencias.length === 0;

  // E90.F4: a seção começa no painel — o que a ADR exige (ressalvas, régua,
  // proveniência) vem ABAIXO da tabela. As listas de convergências e
  // divergências saíram: o painel é a forma delas.
  return (
    <div className="space-y-sc-3" data-testid="conferencia-parecer" data-leitura={parecer.leitura}>
      <PainelDaConferencia caseId={caseId} parecer={parecer} />

      {semAfirmacao ? (
        <p
          className="max-w-prose text-body-sm text-fg-muted"
          data-testid="conferencia-sem-afirmacao"
        >
          {t.semAfirmacao}
        </p>
      ) : null}

      <Lista
        titulo={t.secoes.ressalvas}
        vazio={t.vazio.ressalvas}
        itens={parecer.ressalvas}
        testid="conferencia-ressalvas"
      />

      <Proveniencia parecer={parecer} />

      {parMudou ? (
        <p
          role="status"
          className="max-w-prose text-body-sm text-fg"
          data-testid="conferencia-par-mudou"
        >
          {t.parMudou}
        </p>
      ) : null}
      {recorteMudou ? (
        <p
          role="status"
          className="max-w-prose text-body-sm text-fg"
          data-testid="conferencia-recorte-mudou"
        >
          {t.recorteMudou}
        </p>
      ) : null}
    </div>
  );
}

/**
 * O corpo da seção: o parecer (com a conclusão no rodapé do painel) ou, sem
 * parecer, a leitura resolvida pelo servidor e o que falta fazer.
 *
 * Componente próprio porque a seção externa já carrega estado, efeito e
 * tratamento de erro — juntar o desenho dos cinco estados no mesmo lugar
 * estourava o limite de complexidade do lint, que aqui está fazendo o trabalho
 * dele: quem lê a tela quer saber "em que estado eu estou", e a resposta não
 * pode estar espalhada em nove ternários encadeados.
 */
function CorpoDaConferencia({
  caseId,
  dados,
}: {
  caseId: string;
  dados: ConferenciaFacialData;
}): JSX.Element {
  const { leitura, parecer, tem_par_eleito: temPar } = dados;

  return (
    <>
      {leitura !== null && parecer === null ? (
        <p
          className={`text-body-lg ${_TOM[leitura] ?? 'text-fg'}`}
          data-testid="conferencia-leitura"
        >
          {t.leituras[leitura]}
        </p>
      ) : null}

      {!temPar && parecer === null ? (
        <p className="max-w-prose text-body-sm text-fg-muted" data-testid="conferencia-sem-par">
          {t.semParTexto}
        </p>
      ) : null}

      {parecer !== null ? (
        <ParecerDaConferencia
          caseId={caseId}
          parecer={parecer}
          parMudou={dados.par_mudou}
          recorteMudou={dados.recorte_mudou === true}
        />
      ) : null}

      {temPar && parecer === null ? (
        <p className="text-body-sm text-fg-muted" data-testid="conferencia-pronta">
          {t.pronta}
        </p>
      ) : null}
    </>
  );
}

/** O rótulo do botão diz o que vai acontecer, não o que está acontecendo. */
function rotuloDaAcao(conferindo: boolean, temParecer: boolean): string {
  if (conferindo) return t.conferindo;
  return temParecer ? t.refazer : t.conferir;
}

/**
 * E91.F1 (GUS-51 CA01): o estado da etapa fotográfica, em três valores. É a
 * página que o declara no topo; a seção só sabe dizer em qual ela está.
 */
export type EstadoDaConferencia = 'realizada' | 'pendente' | 'nao_realizada';

export function estadoDaConferencia(dados: ConferenciaFacialData): EstadoDaConferencia {
  if (dados.parecer !== null) return 'realizada';
  return dados.tem_par_eleito ? 'pendente' : 'nao_realizada';
}

export interface ConferenciaFacialProps {
  caseId: string;
  /** O perito concluiu alguma peça: o material do exame não muda mais. */
  concluido: boolean;
  /** Muda quando as fotos do caso mudam — força a releitura do par. */
  versaoDasFotos: number;
  /** E91.F1: avisa a página a cada leitura do estado da conferência. */
  onEstado?: ((estado: EstadoDaConferencia) => void) | undefined;
}

export function ConferenciaFacial({
  caseId,
  concluido,
  versaoDasFotos,
  onEstado,
}: ConferenciaFacialProps): JSX.Element {
  const client = useApiClient();
  const [dados, setDados] = useState<ConferenciaFacialData | null>(null);
  const [erro, setErro] = useState<string | null>(null);
  const [conferindo, setConferindo] = useState(false);
  const [refeita, setRefeita] = useState(false);
  // E91.F1.C01 (F-E91.F1-01): a seção pode ficar montada com outro `caseId`.
  // O parecer, o erro e a ação em voo são do caso: zeram no render, e toda
  // continuação assíncrona confere o caso vigente antes de escrever.
  const casoVigente = useTrocaDeCaso(caseId, () => {
    setDados(null);
    setErro(null);
    setConferindo(false);
    setRefeita(false);
  });

  useEffect(() => {
    if (dados !== null) onEstado?.(estadoDaConferencia(dados));
  }, [dados, onEstado]);

  useEffect(() => {
    let cancelado = false;
    void (async () => {
      try {
        const lido = await lerConferenciaFacial(client, caseId);
        if (!cancelado) setDados(lido);
      } catch {
        if (!cancelado) setErro(t.erros.carregar);
      }
    })();
    return () => {
      cancelado = true;
    };
  }, [client, caseId, versaoDasFotos]);

  const conferir = useCallback((): void => {
    // A conferência é deste caso: se a tela trocou de caso enquanto ela rodava,
    // o desfecho — parecer, releitura ou erro — não escreve na tela do novo.
    const aindaEDesteCaso = (): boolean => casoVigente.current === caseId;
    const refazendo = dados?.parecer != null;
    void (async () => {
      setConferindo(true);
      setErro(null);
      setRefeita(false);
      try {
        await executarConferenciaFacial(client, caseId);
        if (aindaEDesteCaso()) setRefeita(refazendo);
        // Relê o estado inteiro, e não só o parecer devolvido: `par_mudou` é
        // uma comparação com o par de AGORA, e o corpo da escrita não a carrega.
        const lido = await lerConferenciaFacial(client, caseId);
        if (aindaEDesteCaso()) setDados(lido);
      } catch (err) {
        // Rede/timeout: o servidor pode ter concluído e gravado o parecer depois
        // que o navegador desistiu (E82.F5). Relê ANTES de avisar, para a tela
        // mostrar o que existe em vez de induzir uma retentativa que duplica.
        if (err instanceof ExameEletronicoError && err.reason === 'network') {
          try {
            const lido = await lerConferenciaFacial(client, caseId);
            if (aindaEDesteCaso()) setDados(lido);
          } catch {
            // O aviso abaixo cobre: a releitura é melhor esforço.
          }
        }
        if (aindaEDesteCaso()) setErro(mensagemDoErro(err));
      } finally {
        if (aindaEDesteCaso()) setConferindo(false);
      }
    })();
  }, [client, caseId, casoVigente, dados]);

  return (
    <Blueprint
      as="section"
      className="space-y-sc-4 p-sc-6"
      data-testid="conferencia-facial"
      data-leitura={dados?.leitura ?? ''}
      aria-labelledby="conferencia-facial-titulo"
    >
      <div>
        <h3 id="conferencia-facial-titulo" className="text-body-lg text-fg">
          {t.titulo}
        </h3>
        <p className="mt-1 max-w-prose text-body-sm text-fg-muted">{t.intro}</p>
      </div>

      {erro !== null ? (
        <p role="alert" className="text-fg-danger text-body-sm" data-testid="conferencia-erro">
          {erro}
        </p>
      ) : null}

      {refeita ? (
        <p role="status" className="text-body-sm text-fg" data-testid="conferencia-refeita">
          {t.refeita}
        </p>
      ) : null}

      {dados !== null ? <CorpoDaConferencia caseId={caseId} dados={dados} /> : null}

      {dados?.tem_par_eleito === true ? (
        <Button
          type="button"
          variant="secondary"
          size="sm"
          disabled={(concluido && dados.parecer === null) || conferindo}
          onClick={conferir}
          data-testid="conferencia-conferir"
        >
          {rotuloDaAcao(conferindo, dados.parecer !== null)}
        </Button>
      ) : null}

      <p className="max-w-prose text-caption text-fg-dim" data-testid="conferencia-regua-etica">
        {t.reguaEtica}
      </p>
    </Blueprint>
  );
}
